import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { mediaService } from '../../../lib/services';

const SidebarPanel = ({ 
  activeTab, 
  onTabChange, 
  pageTitle, 
  onPageTitleChange,
  pageStatus,
  onPageStatusChange,
  seoTitle,
  onSeoTitleChange,
  seoDescription,
  onSeoDescriptionChange,
  seoKeywords,
  onSeoKeywordsChange,
  onPublish,
  onInsertMedia
}) => {
  const [recentMedia, setRecentMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const tabs = [
    { id: 'settings', icon: 'Settings', label: 'Settings' },
    { id: 'seo', icon: 'TrendingUp', label: 'SEO' },
    { id: 'media', icon: 'Image', label: 'Media' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'review', label: 'In Review' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'published', label: 'Published' }
  ];

  useEffect(() => {
    if (activeTab === 'media') {
      loadRecentMedia();
    }
  }, [activeTab]);

  const loadRecentMedia = async () => {
    setMediaLoading(true);
    const { data } = await mediaService?.getAll({ mediaType: 'image' });
    setRecentMedia((data || [])?.slice(0, 6));
    setMediaLoading(false);
  };

  const handleInsertMedia = (item, publicUrl) => {
    setShowMediaPicker(false);
    onInsertMedia?.({ url: publicUrl, alt: item?.altText || item?.originalName, name: item?.originalName });
  };

  return (
    <div className="w-full lg:w-80 bg-card border-l border-border flex flex-col">
      <div className="border-b border-border">
        <div className="flex">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => onTabChange(tab?.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="hidden md:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {activeTab === 'settings' && (
          <div className="space-y-4 md:space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 md:mb-4">Page Settings</h3>
              <div className="space-y-3 md:space-y-4">
                <Input
                  label="Page Title"
                  type="text"
                  placeholder="Enter page title"
                  value={pageTitle}
                  onChange={(e) => onPageTitleChange(e?.target?.value)}
                  required
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={pageStatus}
                  onChange={onPageStatusChange}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3">Publishing</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs md:text-sm">
                  <span className="text-muted-foreground">Last saved</span>
                  <span className="text-foreground">2 minutes ago</span>
                </div>
                <div className="flex items-center justify-between text-xs md:text-sm">
                  <span className="text-muted-foreground">Word count</span>
                  <span className="text-foreground">1,247</span>
                </div>
                <div className="flex items-center justify-between text-xs md:text-sm">
                  <span className="text-muted-foreground">Reading time</span>
                  <span className="text-foreground">5 min</span>
                </div>
              </div>
              <Button
                variant="default"
                fullWidth
                iconName="Upload"
                iconPosition="left"
                onClick={onPublish}
                className="mt-4"
              >
                Publish Now
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="space-y-4 md:space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 md:mb-4">SEO Optimization</h3>
              <div className="space-y-3 md:space-y-4">
                <Input
                  label="SEO Title"
                  type="text"
                  placeholder="Enter SEO title"
                  description="Recommended: 50-60 characters"
                  value={seoTitle}
                  onChange={(e) => onSeoTitleChange(e?.target?.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Meta Description
                  </label>
                  <textarea
                    placeholder="Enter meta description"
                    value={seoDescription}
                    onChange={(e) => onSeoDescriptionChange(e?.target?.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground placeholder:text-muted-foreground resize-none"
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 150-160 characters
                  </p>
                </div>
                <Input
                  label="Focus Keywords"
                  type="text"
                  placeholder="Enter keywords separated by commas"
                  value={seoKeywords}
                  onChange={(e) => onSeoKeywordsChange(e?.target?.value)}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3">SEO Score</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Overall Score</span>
                  <span className="text-lg font-semibold text-success">85/100</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: '85%' }} />
                </div>
                <div className="space-y-2 text-xs md:text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-foreground">Title length optimal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-foreground">Keywords present</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} className="text-warning" />
                    <span className="text-foreground">Add more internal links</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'media' && (
          <div className="space-y-4 md:space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 md:mb-4">Media Library</h3>
              <Button
                variant="outline"
                fullWidth
                iconName="Image"
                iconPosition="left"
                onClick={() => setShowMediaPicker(true)}
              >
                Browse & Insert Media
              </Button>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">Recent Images</h3>
                <button
                  onClick={loadRecentMedia}
                  className="text-xs text-primary hover:underline"
                >
                  Refresh
                </button>
              </div>
              {mediaLoading ? (
                <div className="flex items-center justify-center h-20">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : recentMedia?.length === 0 ? (
                <div className="text-center py-6">
                  <Icon name="Image" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No images uploaded yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {recentMedia?.map((item) => {
                    const publicUrl = mediaService?.getPublicUrl(item?.filePath);
                    return (
                      <div
                        key={item?.id}
                        onClick={() => handleInsertMedia(item, publicUrl)}
                        className="aspect-square rounded-lg bg-muted border border-border overflow-hidden cursor-pointer hover:border-primary transition-colors group"
                        title={item?.originalName}
                      >
                        {publicUrl ? (
                          <img
                            src={publicUrl}
                            alt={item?.altText || item?.originalName}
                            className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Image" size={20} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Media Picker Modal */}
      {showMediaPicker && (
        <MediaPickerModal
          onSelect={handleInsertMedia}
          onClose={() => setShowMediaPicker(false)}
        />
      )}
    </div>
  );
};

// Inline media picker modal for the editor
const MediaPickerModal = ({ onSelect, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    setLoading(true);
    const { data } = await mediaService?.getAll();
    setItems(data || []);
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const { data } = await mediaService?.upload(file, 'uploads');
      if (data) setItems(prev => [data, ...prev]);
    }
    setUploading(false);
    e.target.value = '';
  };

  const filtered = items?.filter(item =>
    !search || item?.originalName?.toLowerCase()?.includes(search?.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Insert Media</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="flex items-center gap-3 p-3 border-b border-border">
          <div className="relative flex-1">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search media..."
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted border border-border rounded-lg focus:outline-none"
            />
          </div>
          <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,application/pdf" onChange={handleUpload} className="hidden" />
          <button
            onClick={() => fileInputRef?.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {uploading ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="Upload" size={14} />}
            Upload
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Icon name="Image" size={36} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No media found. Upload files to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {filtered?.map(item => {
                const publicUrl = mediaService?.getPublicUrl(item?.filePath);
                const isImage = item?.mimeType?.startsWith('image/');
                return (
                  <div
                    key={item?.id}
                    onClick={() => onSelect(item, publicUrl)}
                    className="aspect-square rounded-lg bg-muted border border-border overflow-hidden cursor-pointer hover:border-primary hover:shadow-md transition-all group"
                    title={item?.originalName}
                  >
                    {isImage && publicUrl ? (
                      <img src={publicUrl} alt={item?.originalName} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-1">
                        <Icon name={item?.mimeType?.startsWith('video/') ? 'Video' : 'FileText'} size={20} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground truncate w-full text-center">{item?.originalName?.split('.')?.pop()?.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SidebarPanel;